import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../shared/Modal';
import { kpiService } from '../../services/kpiService';
import { performanceService } from '../../services/performanceService';
import { piAdminService } from '../../services/piAdminService';
import { reportService } from '../../services/reportService';
import { reportAdminService } from '../../services/reportAdminService';
import { submissionService } from '../../services/submissionService';
import { mapSupabaseError } from '../../utils/supabaseErrors';
import { formatPeriodLabel } from './periodFormat';
import { ExpenseItemsTable } from './ExpenseItemsTable';

const num = (v) => (v == null ? '—' : Number(v).toLocaleString('en-US'));

export function ReviewSubmissionModal({ submission, canDisapprove, onActioned, onClose }) {
  const [items, setItems] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [comment, setComment] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const isPerformance = submission.type === 'performance';
  const isReport = submission.type === 'report';

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (isReport) {
          const [fields, content] = await Promise.all([
            reportAdminService.getReportSystemFields(submission.report_system_id),
            reportService.getSubmissionReport(submission.id),
          ]);
          if (!active) return;
          const byId = content.reduce((m, r) => ({ ...m, [r.report_field_id]: r.value }), {});
          const merged = [...fields]
            .filter((f) => byId[f.id] != null)
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
            .map((f) => ({ id: f.id, label: f.label, value: byId[f.id] }));
          setItems(merged);
        } else if (isPerformance) {
          const [params, perf, exp] = await Promise.all([
            piAdminService.getPIParameters(),
            performanceService.getSubmissionPerformance(submission.id),
            performanceService.getExpenseItems(submission.id),
          ]);
          if (!active) return;
          setExpenses(exp || []);
          const byId = perf.reduce((m, r) => ({ ...m, [r.pi_parameter_id]: r.value }), {});
          const merged = [...params]
            .filter((p) => byId[p.id] != null)
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
            .map((p) => ({ id: p.id, code: p.code, name: p.name, unit: p.unit, value: byId[p.id] }));
          setItems(merged);
        } else {
          // The submitted kpis rows are self-contained (metric_name / target_value /
          // current_value), so a reviewer needs only kpis -- NOT kpi_definitions or
          // kpi_scoring_rules, which RLS exposes only to users assigned to the system
          // (a Supervisor reviewing a supervisee is not assigned, so those read empty).
          const rows = await kpiService.getSubmissionKpis(submission.id);
          if (!active) return;
          setItems([...rows].sort((a, b) => (a.metric_name || '').localeCompare(b.metric_name || '')));
        }
      } catch (e) {
        if (active) setError(mapSupabaseError(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [submission.id, submission.report_system_id, isPerformance, isReport]);

  const run = async (fn) => {
    try { setBusy(true); setError(''); await fn(); toast.success('Done'); setBusy(false); onActioned(); }
    catch (e) { setError(mapSupabaseError(e)); setBusy(false); }
  };

  const handleApprove = () => run(() => submissionService.approve(submission.id));
  const handleReject = () => {
    if (!comment.trim()) { setError('A rejection comment is required.'); return; }
    run(() => submissionService.reject(submission.id, comment.trim()));
  };
  const handleDisapprove = () => run(() => submissionService.disapprove(submission.id, note.trim() || null));

  const isPending = submission.status === 'submitted';
  const isApproved = submission.status === 'approved';
  const isRejected = submission.status === 'rejected';

  // Compute Net deposit for performance submissions
  const valueOf = (code) => {
    const r = items.find((x) => x.code === code);
    return r ? Number(r.value) : null;
  };
  const dep = isPerformance ? valueOf('deposits') : null;
  const wd = isPerformance ? valueOf('withdrawals') : null;
  const netDeposit = isPerformance
    ? (dep == null && wd == null ? null : (dep || 0) - (wd || 0))
    : null;

  return (
    <Modal onClose={onClose} role="dialog" aria-modal="true" aria-label="Review submission">
      <h3>Review submission</h3>
      <p className="form-label"><strong>{submission.submitter?.name}</strong> · {formatPeriodLabel(submission.period, submission.period_start, submission.period_end)}</p>
      {isRejected && submission.review_comment && (
        <div className="error-message" role="alert"><strong>Reviewer note:</strong> {submission.review_comment}</div>
      )}
      {error && <div className="error-message" role="alert">{error}</div>}

      {loading ? <p className="loading-msg">Loading…</p> : (
        <>
          {isReport ? (
            <table className="kpi-bundle-table">
              <thead><tr><th>Field</th><th>Value</th></tr></thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id}>
                    <th scope="row">{r.label}</th>
                    <td className="report-value">{r.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : isPerformance ? (
            <>
              <table className="kpi-bundle-table">
                <thead><tr><th>Parameter</th><th>Value</th><th>Unit</th></tr></thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id}>
                      <td>{r.name}</td>
                      <td>{num(r.value)}</td>
                      <td>{r.unit}</td>
                    </tr>
                  ))}
                  <tr>
                    <td><strong>Net deposit</strong></td>
                    <td>{num(netDeposit)}</td>
                    <td>USD</td>
                  </tr>
                </tbody>
              </table>
              {expenses.length > 0 && <ExpenseItemsTable rows={expenses} />}
            </>
          ) : (
            items.length === 0 ? (
              <p className="submissions-empty">No KPI values were submitted.</p>
            ) : (
              <table className="kpi-bundle-table">
                <thead><tr><th>KPI</th><th>Target</th><th>Value</th><th>Met</th></tr></thead>
                <tbody>
                  {items.map((it) => {
                    const hasVal = it.current_value !== undefined && it.current_value !== null;
                    const met = hasVal && Number(it.current_value) >= Number(it.target_value);
                    // Value and the met-indicator stay in SEPARATE cells so each cell is
                    // pure text (a combined "9 ✓" cell breaks exact-match getByText('9')).
                    return (
                      <tr key={it.id || it.kpi_definition_id}>
                        <td>{it.metric_name || '—'}</td>
                        <td>{it.target_value ?? '—'}</td>
                        <td>{hasVal ? it.current_value : '—'}</td>
                        <td>{hasVal ? (met ? '✓' : '✗') : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
          )}

          {isPending && (
            <div className="form-group">
              <label htmlFor="review-reject-reason">Reject reason (required to reject)</label>
              <textarea id="review-reject-reason" rows={2} maxLength={1000} value={comment}
                onChange={(e) => { setComment(e.target.value); if (error) setError(''); }} />
            </div>
          )}
          {isApproved && canDisapprove && (
            <div className="form-group">
              <label htmlFor="review-disapprove-note">Disapprove note (optional)</label>
              <textarea id="review-disapprove-note" rows={2} maxLength={1000} value={note}
                onChange={(e) => setNote(e.target.value)} />
            </div>
          )}

          <div className="modal-buttons">
            {isPending && (
              <>
                <button type="button" className="btn-edit" onClick={handleApprove} disabled={busy}>Approve</button>
                <button type="button" className="btn-secondary" onClick={handleReject} disabled={busy}>Reject</button>
              </>
            )}
            {isApproved && canDisapprove && (
              <button type="button" className="btn-secondary" onClick={handleDisapprove} disabled={busy}>Disapprove</button>
            )}
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Close</button>
          </div>
        </>
      )}
    </Modal>
  );
}
